import "construct-style-sheets-polyfill";

import { App } from "@app/app/components/App";
import { configManager } from "@app/app/services/configuration/ConfigManager";
import { log } from "@app/app/services/log";
import * as Sentry from "@sentry/react";
import { observe } from "@twind/core";
import { createRoot } from "react-dom/client";

import { AppElementsProviderEventTypes } from "./app/context/AppElementsProviderEventTypes";
import { appElementsProvider } from "./app/services/appElements";

/**
 * Main
 */
export let shadowRoot: ShadowRoot;

const releaseDescription = `${process.env.VITE_GIT_TAG}, date ${process.env.VITE_GIT_DATE} (${process.env.VITE_GIT_SHORT})`;

log.info(`Version ${releaseDescription})`);

const SENTRY_ENABLED = true; // TOO many requests to sentry, so disabled for now

export const bootstrapper = (): ShadowRoot => {
    appElementsProvider.initializeAppElements();

    if (SENTRY_ENABLED) {
        /**
         * @note - Sentry has major impact in build size:
         * ~215kB (67kB gzip)
         * @todo - 1. upgrade ws2 sentry version to same as IBE
         *         2. implement shared Sentry library if possible
         */
        Sentry.init({
            dsn: configManager.config.analytics.sentryDsn,
            integrations: [
                // send console.log, console.warn, and console.error calls as logs to Sentry
                // Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
            ],
            // tracesSampleRate: 0.25, // 25% of events will be logged (to be fine tuned)
            // replaysSessionSampleRate: 0.1,
            // replaysOnErrorSampleRate: 1.0,
            ignoreErrors: [/ReferenceError: fbq is not defined/, /Can't find variable: fbq/],
            enableLogs: true,
            enabled: true,
            environment: process.env.NODE_ENV,
            beforeSend(event) {
                // TOO many requests to sentry, so disabled for now
                return null;
            },
        });

        Sentry.setTag("hc_ibe_release", releaseDescription);
    }

    let handleOnAppContainerElementRemovedListerenerDetacher: () => void;

    customElements.define(
        "hc-ibe",
        class extends HTMLElement {
            public connectedCallback() {
                const { sheet, tw, ibeInstanceKey, container } = appElementsProvider.getAppElements();

                shadowRoot = this.attachShadow({ mode: "open" });

                // shadowRoot.childNodes.forEach((childNode) => {
                //     this.appendChild(childNode);
                // });

                // shadow DOM as react root
                const root = createRoot(shadowRoot);

                // render react element inside shadow DOM
                root.render(
                    <div bablic-exclude="" key={ibeInstanceKey}>
                        <App />
                    </div>
                );

                if (!sheet || !tw) {
                    throw new Error("Not all required app elements are initialized");
                }

                // link sheet target to shadow dom root
                shadowRoot.adoptedStyleSheets = [sheet.target];

                // finally, observe using tw function
                observe(tw, shadowRoot);

                handleOnAppContainerElementRemovedListerenerDetacher = appElementsProvider.on(
                    AppElementsProviderEventTypes.ContainerRemoved,
                    () => {
                        handleOnAppContainerElementRemovedListerenerDetacher();

                        setTimeout(() => {
                            const errorMessage = `App container removed for the ${appElementsProvider.getInitRetryCount()} / ${appElementsProvider.getInitRetryLimit()} time(s)`;

                            log.error(errorMessage);

                            Sentry.captureMessage(errorMessage);
                        }, 1000);

                        root.unmount();

                        container?.remove();
                    }
                );
            }
        }
    );

    return shadowRoot;
};
